import { useMemo } from 'react';

import { formatUnits } from 'viem';

import { I_Winner } from '@/app/[locale]/(main)/profile/_components/ProfilePredictionDialog/WinnerFlashCard';
import { formatNumberWithCommasAndDecimals } from '@/lib/utils/formatNumberWithCommasAndDecimals';
import { formatTimeAgoFromStart } from '@/lib/utils/formatTimeAgoString';
import { C_USDC_DECIMALS } from '@/ts/constants/C_USDC_DECIMALS';
import { T_PredictionWinner } from '@/ts/types/T_PredictionWinners';
import { T_WinnersData } from '@/ts/types/T_WinnersData';

interface I_UseWinnersTransformProps {
  primeData: T_WinnersData | null;
  flashData: T_WinnersData | null;
}

export const useWinnersTransform = ({
  primeData,
  flashData
}: I_UseWinnersTransformProps) => {
  const transformWinners = useMemo(() => {
    const effectiveStartTime =
      primeData?.prediction?.startTime || flashData?.prediction?.startTime;

    const transformWinner = (
      winner: T_PredictionWinner,
      index: number,
      tab: 'prime' | 'flash'
    ): I_Winner => {
      const rewardAmountRaw =
        tab === 'prime' ? winner.rewardAmount : winner.flashRewardAmount;
      const rewardAmountDecimal = formatUnits(
        BigInt(rewardAmountRaw),
        C_USDC_DECIMALS
      );

      const entryTime = effectiveStartTime
        ? formatTimeAgoFromStart(effectiveStartTime, winner.submittedAt)
        : '1 min';

      const wonCount = formatNumberWithCommasAndDecimals(
        rewardAmountDecimal,
        2
      );

      const rawWinningPricesWonCount = Number(wonCount) / winner.flashWinsCount;
      const truncatedWinningPricesWonCount =
        Math.floor(rawWinningPricesWonCount * 100) / 100;

      const winningPrice = primeData?.items[0]?.predictedPrice || 0;
      const winningSubmissionsFormatted = winner.winningSubmissions
        .map(item => {
          const entryTime = effectiveStartTime
            ? formatTimeAgoFromStart(
                effectiveStartTime,
                new Date(item.submittedAt * 1000).toISOString()
              )
            : '1 min';

          return {
            ...item,
            entryTime
          };
        })
        .filter(item => item.price !== winningPrice);

      return {
        rank: index + 1,
        name: winner.nickname,
        predicted: formatNumberWithCommasAndDecimals(winner.predictedPrice),
        won: wonCount,
        entryTime,
        userAddress: winner.userAddress,
        avatar: winner.avatar,
        winningSubmissions: winningSubmissionsFormatted,
        winningPricesWon: truncatedWinningPricesWonCount
      };
    };

    return {
      prime:
        primeData?.items.map((w, i) => transformWinner(w, i, 'prime')) || [],
      flash:
        flashData?.items.map((w, i) => transformWinner(w, i, 'flash')) || []
    };
  }, [primeData, flashData]);

  return { transformWinners };
};
